import create from "zustand";
import { persist } from "zustand/middleware";

import {
  fetchPendingAndActiveSurveysApi,
  fetchShowSurveyResults,
  sendSurveyResultApi,
} from "../api/surverysApi";
import { toast } from "react-toastify";
import { TOAST_CONTAINER_LAYOUT } from "../../../util/Constants";
import { isNil } from "lodash";
import { format } from "date-fns";
import { SHOW_SURVEY_RESULTS } from "../lib/results";

export const SURVEY_PERSISTANT_STORE = "survey-store";

const INITIAL_STATE = {
  showSurveysDialog: false,
  loadingSurverys: false,
  surveys: [],
  closedSurveyInMiddle: false,

  //first log
  continuePressed: false,
  skipPressed: false,
  skipPressedDay: null,

  // send result
  loadingSendResults: false,
  showThankYou: false,

  // show results
  loadingShowSurveysResults: false,
  showSurveyResultSkipped: false,
  showSurveyResult: false,
};

export const CURRENT_DATE = format(new Date(), "yyyy-MM-dd");

const useSurveysStore = create(
  persist(
    (set) => ({
      ...INITIAL_STATE,

      // FETCH SURVEYS
      fetchSurveys: async ({ t, headers, logout }) => {
        set({ loadingSurverys: true });
        try {
          const response = await fetchPendingAndActiveSurveysApi({
            headers,
            logout,
          });

          if (response != null) {
            const { ok, data, error } = response;
            if (ok && !isNil(data)) {
              set(() => ({
                surveys: data,
                showSurveysDialog: true,
              }));
            } else {
              toast.error(error, {
                containerId: TOAST_CONTAINER_LAYOUT,
              });
            }
          } else {
            toast.error(t("ERROR_RESOURCE_NOT_FOUND_TEXT"), {
              containerId: TOAST_CONTAINER_LAYOUT,
            });
          }
        } catch (e) {
          toast.error(e);
        } finally {
          set({ loadingSurverys: false });
        }
      },
      // SEND SURVEY RESULT
      sendSurveyResult: async ({
        t,
        headers,
        logout,
        comment,
        ratings,
        surveyCode,
      }) => {
        set({ loadingSendResults: true });
        try {
          const body = {
            comment,
            ratings,
          };
          const response = await sendSurveyResultApi({
            headers,
            logout,
            body,
            surveyCode,
          });

          if (response != null) {
            const { ok, data, error } = response;
            if (ok && !isNil(data)) {
              set(() => ({
                showThankYou: true,
              }));
            } else {
              toast.error(error, {
                containerId: TOAST_CONTAINER_LAYOUT,
              });
            }
          } else {
            toast.error(t("ERROR_RESOURCE_NOT_FOUND_TEXT"), {
              containerId: TOAST_CONTAINER_LAYOUT,
            });
          }
        } catch (e) {
          toast.error(e);
        } finally {
          set({ loadingSendResults: false });
        }
      },

      // Dialog behaviour
      openSurveyDialog: () =>
        set(() => ({
          skipPressed: false,
          showSurveysDialog: true,
          closedSurveyInMiddle: false,
        })),
      closeSurveyDialog: () =>
        set(() => ({
          showSurveysDialog: false,
          closedSurveyInMiddle: true,
        })),

      // Behaviour of user wants or not to complete de survey
      continueSurvey: () =>
        set(() => ({
          continuePressed: true,
        })),
      skipSurvey: () =>
        set(() => ({
          skipPressed: true,
          skipPressedDay: CURRENT_DATE,
        })),

      // ****************************************
      // Surveys results
      // ****************************************
      fetchShowSurveysResults: async ({ t, headers, logout }) => {
        if (SHOW_SURVEY_RESULTS === false) {
          return;
        }
        set({ loadingShowSurveysResults: true });
        try {
          const response = await fetchShowSurveyResults({
            headers,
            logout,
          });

          if (response != null) {
            const { ok, data, error } = response;
            if (ok && !isNil(data)) {
              set(() => ({
                showSurveyResult: data?.showResults === true,
              }));
            } else {
              toast.error(error, {
                containerId: TOAST_CONTAINER_LAYOUT,
              });
            }
          } else {
            toast.error(t("ERROR_RESOURCE_NOT_FOUND_TEXT"), {
              containerId: TOAST_CONTAINER_LAYOUT,
            });
          }
        } catch (e) {
          toast.error(e);
        } finally {
          set({ loadingShowSurveysResults: false });
        }
      },
      openShowSurveyResultDialog: () =>
        set(() => ({
          showSurveyResultSkipped: false,
          showSurveyResult: true,
        })),
      closeShowSurveyResultDialog: () =>
        set(() => ({
          showSurveyResultSkipped: true,
        })),
      recoverShowSurveyResultSkippedState: (toRecover) => {
        set(() => ({
          showSurveyResultSkipped: toRecover,
        }));
      },

      // TO RESET THE STORE TO INITIAL STATE
      resetSurveyStore: () => {
        set(INITIAL_STATE);
      },
    }),
    {
      name: SURVEY_PERSISTANT_STORE,
      partialize: (state) => ({
        skipPressed: state.skipPressed,
        skipPressedDay: state.skipPressedDay,
        closedSurveyInMiddle: state.closedSurveyInMiddle,
        showSurveyResultSkipped: state.showSurveyResultSkipped,
      }),
    }
  )
);

export { useSurveysStore };
